import type { UseZoomableReturnType } from "./zoomable";
import { type Ref } from "vue";
import type { Point } from "@/stores/line";
import type Konva from "konva";
import { useEditorStore } from "@/stores/editor_store";
import { inject } from "vue";
import type { PersistentStore } from "@/stores/persistent_store";

/**
 * useMouseZoom handles all mouse-related zoom functionality
 */
export function useMouseZoom(zoomable: UseZoomableReturnType, stage: Ref<Konva.Stage>) {
  const currentPersistentStore = inject<Ref<PersistentStore>>("currentPersistentStore");
  const zoomLevels = [300, 600]; // 3x, 6x
  const store = useEditorStore();

  /**
   * Get pointer positions similar to getPointerPositions in editor.vue
   */
  const getPointerPositions = (): { pointerPosition: Point; focusPoint: Point } => {
    const theStage = stage.value.getStage();
    const scale = theStage.scaleX();

    const pointerPosition: Point = theStage.getPointerPosition();
    const focusPoint: Point = {
      x: (pointerPosition.x - theStage.x()) / scale,
      y: (pointerPosition.y - theStage.y()) / scale,
    };

    return { pointerPosition, focusPoint };
  };

  /**
   * Store pointer positions similar to storePointerPositions in editor.vue
   */
  const storePointerPositions = (pointerPosition: Point, focusPoint: Point) => {
    store.selectedImage.meta.last_focus_point = focusPoint;
    store.selectedImage.meta.last_pointer_position = pointerPosition;
    store.updateImage(store.selectedImage);
  };

  const getNextZoomLevel = () => {
    const currentZoomLevel = currentPersistentStore.value?.zoomLevel || 100;
    const currentIndex = zoomLevels.indexOf(currentZoomLevel);

    // Eg: currentIndex = 0, zoomLevels.length = 3 => 0 + 1 % (3 + 1) = 1
    // Eg: currentIndex = 1, zoomLevels.length = 3 => 1 + 1 % (3 + 1) = 2
    // Eg: currentIndex = 2, zoomLevels.length = 3 => 2 + 1 % (3 + 1) = 3

    const nextIndex = (currentIndex + 1) % (zoomLevels.length + 1);
    return nextIndex < zoomLevels.length ? zoomLevels[nextIndex] : 100;
  };

  /**
   * Handle click-to-zoom functionality: 1x → 3x → 6x → reset
   * Zooms to cursor position instead of center
   * Zoom levels are isolated per image
   */
  const handleClickToZoom = () => {
    const { focusPoint, pointerPosition } = getPointerPositions();
    storePointerPositions(pointerPosition, focusPoint);

    const nextZoomLevel = getNextZoomLevel();
    if (nextZoomLevel === 100) {
      zoomable.resetZoom();
    } else {
      zoomable.setZoom(nextZoomLevel);
      zoomable.applyZoomWithFocusPoint(focusPoint, pointerPosition, { animate: true });
    }
  };

  const handleButtonZoom = () => {
    const nextZoomLevel = getNextZoomLevel();
    if (nextZoomLevel === 100) {
      zoomable.resetZoom();
    } else {
      zoomable.setZoom(nextZoomLevel);
      zoomable.applyZoom();
      zoomable.repositionAfterCenterZoom();
    }
  };

  return {
    handleClickToZoom,
    handleButtonZoom,
  };
}
